import { useState } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useDestinatario } from '@/hooks/useOrdini'

/**
 * Step 2: Colli della destinazione
 * 
 * Form per creare colli con peso e dimensioni. Il destinatario è unico
 * per la spedizione ed è stato selezionato nello step precedente.
 * 
 * Features:
 * - Peso default 2.5kg
 */
export default function SpedizioneWizardStep2({ data, onComplete, onBack }) {
  const [colli, setColli] = useState(
    data.colli.length > 0
      ? data.colli.map((collo) => ({ ...collo, destinatario_id: data.destinatario_id }))
      : [
          {
            destinatario_id: data.destinatario_id,
            numero_collo: 1,
            peso_kg: '', // vuoto: verra' calcolato allo Step 4 dai contenuti
            lunghezza_cm: '',
            larghezza_cm: '',
            altezza_cm: '',
            valore_dichiarato_cents: 0,
            note: '',
          },
        ]
  )
  
  const [errors, setErrors] = useState({})
  const { data: destinatario } = useDestinatario(data.destinatario_id)

  const addCollo = () => {
    setColli([
      ...colli,
      {
        destinatario_id: data.destinatario_id,
        numero_collo: colli.length + 1,
        peso_kg: '', // vuoto: verra' calcolato allo Step 4 dai contenuti
        lunghezza_cm: '',
        larghezza_cm: '',
        altezza_cm: '',
        valore_dichiarato_cents: 0,
        note: '',
      },
    ])
  }
  
  const removeCollo = (index) => {
    const newColli = colli.filter((_, i) => i !== index)
    // Rinumera i colli
    const renumbered = newColli.map((collo, idx) => ({
      ...collo,
      numero_collo: idx + 1,
    }))
    setColli(renumbered)
  }

  const updateCollo = (index, field, value) => {
    const newColli = [...colli]
    newColli[index] = {
      ...newColli[index],
      [field]: value,
    }
    setColli(newColli)
  }

  const validate = () => {
    const newErrors = {}

    colli.forEach((collo, idx) => {
      if (!data.destinatario_id || collo.destinatario_id !== data.destinatario_id) {
        newErrors[`collo_${idx}_destinatario`] = 'Seleziona un destinatario'
      }
      // Peso facoltativo in questo step: se lasciato vuoto verra' calcolato allo Step 4.
      // Errore solo se l'utente ha inserito un valore invalido (<=0).
      if (collo.peso_kg !== '' && collo.peso_kg !== null && collo.peso_kg !== undefined) {
        if (parseFloat(collo.peso_kg) <= 0) {
          newErrors[`collo_${idx}_peso`] = 'Peso non valido (deve essere > 0)'
        }
      }

      // Validazione dimensioni: se una è valorizzata, tutte devono esserlo
      const dimensioni = [collo.lunghezza_cm, collo.larghezza_cm, collo.altezza_cm]
      const dimensioniValorizzate = dimensioni.filter(d => d && d !== '')
      if (dimensioniValorizzate.length > 0 && dimensioniValorizzate.length < 3) {
        newErrors[`collo_${idx}_dimensioni`] = 'Specifica tutte le dimensioni o nessuna'
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validate()) {
      // Converti i valori in numeri (peso puo' essere '' se l'utente non lo ha impostato,
      // verra' pre-compilato da calcola-peso-colli allo Step 4).
      const colliProcessed = colli.map(collo => ({
        ...collo,
        peso_kg: collo.peso_kg === '' || collo.peso_kg === null || collo.peso_kg === undefined
          ? ''
          : parseFloat(collo.peso_kg),
        lunghezza_cm: collo.lunghezza_cm ? parseInt(collo.lunghezza_cm) : null,
        larghezza_cm: collo.larghezza_cm ? parseInt(collo.larghezza_cm) : null,
        altezza_cm: collo.altezza_cm ? parseInt(collo.altezza_cm) : null,
        valore_dichiarato_cents: collo.valore_dichiarato_cents || 0,
      }))

      onComplete({ colli: colliProcessed })
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Colli da Spedire</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Crea i colli per {destinatario?.nome_completo || 'il destinatario selezionato'}
              </p>
            </div>
            <Button type="button" onClick={addCollo} size="sm">
              <PlusIcon className="mr-2 h-4 w-4" />
              Aggiungi Collo
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {colli.map((collo, index) => (
            <Card key={index} className="border-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Collo {collo.numero_collo}</CardTitle>
                  {colli.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCollo(index)}
                    >
                      <TrashIcon className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {errors[`collo_${index}_destinatario`] && (
                  <p className="text-sm text-destructive">
                    {errors[`collo_${index}_destinatario`]}
                  </p>
                )}

                {/* Peso */}
                <div className="space-y-2">
                  <Label htmlFor={`peso_${index}`}>Peso (kg)</Label>
                  <Input
                    id={`peso_${index}`}
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={collo.peso_kg}
                    onChange={(e) => updateCollo(index, 'peso_kg', e.target.value)}
                    placeholder="Calcolato dai contenuti"
                  />
                  {errors[`collo_${index}_peso`] && (
                    <p className="text-sm text-destructive">
                      {errors[`collo_${index}_peso`]}
                    </p>
                  )}
                </div>

                {/* Dimensioni */}
                <div className="space-y-2">
                  <Label>Dimensioni (cm) - Opzionale</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Input
                        type="number"
                        min="1"
                        value={collo.lunghezza_cm}
                        onChange={(e) => updateCollo(index, 'lunghezza_cm', e.target.value)}
                        placeholder="L"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        min="1"
                        value={collo.larghezza_cm}
                        onChange={(e) => updateCollo(index, 'larghezza_cm', e.target.value)}
                        placeholder="W"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        min="1"
                        value={collo.altezza_cm}
                        onChange={(e) => updateCollo(index, 'altezza_cm', e.target.value)}
                        placeholder="H"
                      />
                    </div>
                  </div>
                  {errors[`collo_${index}_dimensioni`] && (
                    <p className="text-sm text-destructive">
                      {errors[`collo_${index}_dimensioni`]}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Se specifichi le dimensioni, inseriscile tutte
                  </p>
                </div>

                {/* Valore Dichiarato */}
                <div className="space-y-2">
                  <Label htmlFor={`valore_${index}`}>Valore Dichiarato (€)</Label>
                  <Input
                    id={`valore_${index}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={collo.valore_dichiarato_cents / 100}
                    onChange={(e) =>
                      updateCollo(
                        index,
                        'valore_dichiarato_cents',
                        Math.round(parseFloat(e.target.value || 0) * 100)
                      )
                    }
                    placeholder="50.00"
                  />
                  <p className="text-xs text-muted-foreground">
                    Per assicurazione (opzionale)
                  </p>
                </div>

                {/* Note */}
                <div className="space-y-2">
                  <Label htmlFor={`note_${index}`}>Note Collo</Label>
                  <Textarea
                    id={`note_${index}`}
                    value={collo.note}
                    onChange={(e) => updateCollo(index, 'note', e.target.value)}
                    placeholder='es: "Fragile", "Deperibile"'
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Indietro
        </Button>
        <Button type="button" onClick={handleNext}>
          Avanti: Assegna Prodotti
        </Button>
      </div>
    </div>
  )
}
